import { useMemo, useState } from 'react';
import LedgerFilters from './components/LedgerFilters';
import LedgerTable from './components/LedgerTable';
import KpiCard from '../components/KpiCard';
import { ledgerEntries } from '@/mocks/members';

export default function TransactionsPage() {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('All');
  const [source, setSource] = useState('All sources');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const typeKey = type.toLowerCase();
    return ledgerEntries.filter((entry) => {
      const matchesQuery =
        !q ||
        entry.member.toLowerCase().includes(q) ||
        entry.description.toLowerCase().includes(q) ||
        entry.id.toLowerCase().includes(q);
      const matchesType = type === 'All' || entry.type === typeKey;
      const matchesSource = source === 'All sources' || entry.source === source;
      return matchesQuery && matchesType && matchesSource;
    });
  }, [query, type, source]);

  const totalEarned = ledgerEntries
    .filter((entry) => entry.points > 0)
    .reduce((sum, entry) => sum + entry.points, 0);
  const totalRedeemed = ledgerEntries
    .filter((entry) => entry.points < 0)
    .reduce((sum, entry) => sum + Math.abs(entry.points), 0);

  const stats = [
    {
      id: 'earned',
      label: 'Points earned',
      value: `+${totalEarned.toLocaleString()}`,
      icon: 'ri-arrow-up-line',
      tone: 'accent' as const,
      delta: 'Inflow',
      trend: 'up' as const,
      note: 'Total points awarded',
    },
    {
      id: 'redeemed',
      label: 'Points redeemed',
      value: `-${totalRedeemed.toLocaleString()}`,
      icon: 'ri-gift-2-line',
      tone: 'secondary' as const,
      delta: 'Outflow',
      trend: 'down' as const,
      note: 'Burned in rewards',
    },
    {
      id: 'net',
      label: 'Net points change',
      value: `${(totalEarned - totalRedeemed).toLocaleString()}`,
      icon: 'ri-scales-3-line',
      tone: 'primary' as const,
      note: 'Net program expansion',
    },
    {
      id: 'events',
      label: 'Ledger events',
      value: ledgerEntries.length.toString(),
      icon: 'ri-history-line',
      tone: 'background' as const,
      note: 'Total immutable entries',
    },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-extrabold text-2xl text-foreground-950 tracking-tight">Points ledger</h3>
          <p className="text-sm text-foreground-500 mt-0.5">Every earn, redeem, and adjustment across all platforms.</p>
        </div>
        <button
          type="button"
          className="whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-background-50 border border-background-200/90 text-sm font-semibold text-foreground-700 shadow-xs hover:bg-background-100 hover:border-background-300 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-download-2-line text-primary-500"></i> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <KpiCard
            key={stat.id}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            tone={stat.tone}
            delta={stat.delta}
            trend={stat.trend}
            note={stat.note}
          />
        ))}
      </div>

      <LedgerFilters
        query={query}
        onQueryChange={setQuery}
        type={type}
        onTypeChange={setType}
        source={source}
        onSourceChange={setSource}
        resultCount={filtered.length}
        total={ledgerEntries.length}
      />

      <LedgerTable data={filtered} />
    </div>
  );
}
